/** Documentation view component explaining how the application works. */

import { useNavigationStore } from '@/stores/navigation';

export function renderDocumentation(): void {
  // Hide all views first
  document.querySelectorAll('.view').forEach(v => {
    v.classList.add('hidden');
    (v as HTMLElement).style.display = 'none';
  });
  
  const docView = document.getElementById('view-documentation');
  if (docView) {
    docView.classList.remove('hidden');
    docView.style.display = '';
  }
  
  // Hide loader
  const mainLoader = document.getElementById('main-loader');
  if (mainLoader) {
    mainLoader.classList.add('hidden');
    mainLoader.style.display = 'none';
  }
  
  // Update navigation store
  useNavigationStore.getState().setCurrentView('documentation');
  
  // Update active nav link
  document.querySelectorAll('.nav-link, .nav-link-mobile').forEach(link => {
    link.classList.remove('active');
    if (link.getAttribute('data-view') === 'documentation') {
      link.classList.add('active');
    }
  });
  
  // Render documentation content
  const container = document.getElementById('documentation-content');
  if (!container) return;
  
  container.innerHTML = `
    <div class="max-w-4xl mx-auto space-y-8">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold mb-4 gradient-text-cyan-purple">
          <span class="emoji">📚</span> How TKRScan Works
        </h1>
        <p class="text-secondary text-lg">
          A comprehensive guide to understanding and using the TKR Stock Scanner
        </p>
      </div>
      
      <!-- Overview Section -->
      <div class="card p-6">
        <h2 class="text-2xl font-bold mb-4 text-neon-cyan">
          <span class="emoji">🔍</span> Overview
        </h2>
        <div class="space-y-4 text-secondary">
          <p>
            <strong class="text-primary">TKRScan</strong> is a Reddit stock mention tracking and analysis tool with frequent data updates. 
            It monitors Reddit for stock ticker mentions, analyzes sentiment, tracks momentum, and provides 
            comprehensive insights to help you stay informed about what's being discussed.
          </p>
          <p>
            The application collects data from Reddit posts and comments, extracts stock ticker symbols, analyzes the 
            sentiment of each mention, and aggregates this information into actionable insights and alerts.
          </p>
        </div>
      </div>
      
      <!-- How It Works Section -->
      <div class="card p-6">
        <h2 class="text-2xl font-bold mb-4 text-neon-purple">
          <span class="emoji">⚙️</span> How It Works
        </h2>
        <div class="space-y-6 text-secondary">
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">1. Data Collection</h3>
            <p>
              The scanner monitors Reddit using the Reddit API (PRAW). It processes posts and comments 
              from configured subreddits on a recurring basis, looking for stock ticker mentions.
            </p>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">2. Ticker Extraction</h3>
            <p>
              Tickers are extracted using multiple methods:
            </p>
            <ul class="list-disc list-inside ml-4 mt-2 space-y-1">
              <li><strong class="text-neon-cyan">$TICKER format:</strong> Explicit mentions like "$AAPL" or "$TSLA" are parsed first</li>
              <li><strong class="text-neon-cyan">Strict ALL CAPS detection:</strong> Standalone 2-5 letter words are considered only when they are ALL CAPS in the original text</li>
              <li><strong class="text-neon-cyan">Filtering:</strong> A configurable common-word filter removes high-noise tokens to reduce false positives</li>
              <li><strong class="text-neon-cyan">Validation:</strong> Extracted symbols are validated against the loaded ticker universe (from <span class="font-mono">all_tickers.txt</span>) before being stored</li>
            </ul>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">3. Sentiment Analysis</h3>
            <p>
              Each mention is analyzed using <strong class="text-neon-cyan">VADER</strong> (Valence Aware Dictionary and sEntiment Reasoner), 
              a sentiment analysis tool specifically attuned to social media text. The sentiment score ranges from -1 (very negative) 
              to +1 (very positive), with 0 being neutral.
            </p>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">4. Data Aggregation</h3>
            <p>
              The system aggregates mentions by ticker, calculating:
            </p>
            <ul class="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Total mention counts over various time windows</li>
              <li>Average sentiment scores</li>
              <li>Velocity (rate of mentions over time)</li>
              <li>Hype scores (composite metric combining mentions, sentiment, and karma)</li>
              <li>Momentum indicators (early signals of increasing activity)</li>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- Features Section -->
      <div class="card p-6">
        <h2 class="text-2xl font-bold mb-4 text-neon-green">
          <span class="emoji">✨</span> Features Explained
        </h2>
        <div class="space-y-6 text-secondary">
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">🏠</span> Dashboard
            </h3>
            <p class="mb-2">The main hub showing key insights and alerts:</p>
            <ul class="list-disc list-inside ml-4 space-y-1">
              <li><strong class="text-neon-pink">Velocity Alerts:</strong> Tickers with rapid mention spikes (thresholds vary by current scanner logic and data window)</li>
              <li><strong class="text-neon-cyan">Momentum Alerts:</strong> Early signals of increasing activity, including gradual acceleration, quality surges, cross-subreddit spread, and DD mention combos</li>
              <li><strong class="text-neon-cyan">Breakout Tickers:</strong> Tickers with significant velocity increases (1hr vs 23hr comparison)</li>
              <li><strong class="text-neon-purple">Sentiment Shift:</strong> Tickers with notable sentiment changes (24hr vs previous 24hr)</li>
              <li><strong class="text-neon-green">Hype Score:</strong> Composite score ranking tickers by overall buzz (mentions, sentiment, karma)</li>
            </ul>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">📡</span> Live Feed
            </h3>
            <p>
              Live feed of Reddit stock mentions from the last 24 hours. You can filter by ticker symbol 
              to see mentions of a specific stock, load more rows, and use the refresh button for the latest batch while staying in the view.
              Each mention shows the ticker, subreddit, author karma, sentiment, timestamp, and a link to the original post/comment.
            </p>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">👑</span> Top Influencers
            </h3>
            <p>
              Reddit users ranked by their impact on stock discussions. Metrics include total mentions, total karma, 
              average karma per mention, and unique tickers mentioned. Click on an influencer to see their detailed 
              profile and mention history.
            </p>
          </div>

          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">🧠</span> Influencer Deep Dive
            </h3>
            <p>
              Detailed influencer profiles include ticker distribution, sentiment history, recent mentions, and aggregate author metrics.
              Use this view to understand whether a user is consistently focused on specific tickers or broad market chatter.
            </p>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">📋</span> Due Diligence Posts
            </h3>
            <p>
              Posts from the last 7 days matched to due diligence signals. These are often in-depth research 
              posts that can significantly impact stock discussions. Posts are ranked by author karma and recency, and
              expanded rows can fetch the full Reddit post content when available.
            </p>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">📰</span> News & Filings
            </h3>
            <p>
              SEC EDGAR filings and related company updates. The system fetches recent filings (8-K, 10-K, 10-Q, etc.),
              supports filing-type filtering/sorting/search, and provides expanded detail views including related filings
              and filing price-status indicators (for freshness context).
            </p>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">🔍</span> Deep Dive
            </h3>
            <p>
              Comprehensive analysis for any ticker. Click on any ticker symbol throughout the app to view:
            </p>
            <ul class="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Market metrics (price, volume, market cap, etc.)</li>
              <li>Hype score trend over time</li>
              <li>Mentions vs price vs sentiment charts</li>
              <li>Sentiment distribution</li>
              <li>Live TradingView price chart</li>
              <li>Most positive and negative mentions</li>
              <li>Recent mentions table</li>
              <li>Related due diligence posts</li>
            </ul>
          </div>
          
          <div>
            <h3 class="text-xl font-semibold text-primary mb-2">
              <span class="emoji">👀</span> Watchlist
            </h3>
            <p>
              Track your favorite tickers. Add stocks to your watchlist to quickly access their deep dive pages.
              Your watchlist is stored in your browser on this device, so it stays personal to your browser profile.
            </p>
          </div>
        </div>
      </div>
      
      <!-- Metrics Explained Section -->
      <div class="card p-6">
        <h2 class="text-2xl font-bold mb-4 text-neon-yellow">
          <span class="emoji">📊</span> Understanding the Metrics
        </h2>
        <div class="space-y-4 text-secondary">
          <div>
            <h3 class="text-lg font-semibold text-primary mb-2">Sentiment Score</h3>
            <p>
              A value between -1 and +1 indicating the emotional tone of a mention:
            </p>
            <ul class="list-disc list-inside ml-4 mt-2 space-y-1">
              <li><strong class="text-red-400">-1.0 to -0.5:</strong> Very negative</li>
              <li><strong class="text-yellow-400">-0.5 to -0.1:</strong> Somewhat negative</li>
              <li><strong class="text-gray-400">-0.1 to +0.1:</strong> Neutral</li>
              <li><strong class="text-yellow-400">+0.1 to +0.5:</strong> Somewhat positive</li>
              <li><strong class="text-green-400">+0.5 to +1.0:</strong> Very positive</li>
            </ul>
          </div>
          
          <div>
            <h3 class="text-lg font-semibold text-primary mb-2">Velocity</h3>
            <p>
              The rate at which a ticker is being mentioned. A velocity increase of 2.5x means the ticker is being 
              mentioned 2.5 times more frequently in the current period compared to the previous period.
            </p>
          </div>
          
          <div>
            <h3 class="text-lg font-semibold text-primary mb-2">Hype Score</h3>
            <p>
              A composite metric that combines multiple factors:
            </p>
            <ul class="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Total mention count</li>
              <li>Average sentiment</li>
              <li>Average author karma (quality of contributors)</li>
              <li>Recency weighting (recent mentions weighted higher)</li>
            </ul>
            <p class="mt-2">
              Higher scores indicate more overall buzz and engagement around a ticker.
            </p>
          </div>
          
          <div>
            <h3 class="text-lg font-semibold text-primary mb-2">Momentum Levels</h3>
            <p>
              Momentum alerts are categorized by intensity:
            </p>
            <ul class="list-disc list-inside ml-4 mt-2 space-y-1">
              <li><strong class="text-yellow-400">Low:</strong> Early signal, worth monitoring</li>
              <li><strong class="text-orange-400">Medium:</strong> Moderate activity increase</li>
              <li><strong class="text-red-400">High:</strong> Significant momentum building</li>
            </ul>
          </div>
          
          <div>
            <h3 class="text-lg font-semibold text-primary mb-2">Momentum Types</h3>
            <ul class="list-disc list-inside ml-4 mt-2 space-y-1">
              <li><strong class="text-neon-cyan">Gradual Acceleration:</strong> Mentions are increasing steadily over time</li>
              <li><strong class="text-neon-cyan">Quality Surge:</strong> Higher karma contributors are mentioning the ticker</li>
              <li><strong class="text-neon-cyan">Cross-Subreddit Spread:</strong> Ticker is being discussed across multiple subreddits</li>
              <li><strong class="text-neon-cyan">DD Mention Combo:</strong> A due diligence post was followed by increasing mentions</li>
            </ul>
          </div>

        </div>
      </div>
      
      <!-- Tips Section -->
      <div class="card p-6 border-2 border-neon-cyan neon-glow-cyan">
        <h2 class="text-2xl font-bold mb-4 text-neon-cyan">
          <span class="emoji">💡</span> Tips for Using TKRScan
        </h2>
        <div class="space-y-3 text-secondary">
          <div class="flex items-start">
            <span class="emoji mr-2">1️⃣</span>
            <p><strong class="text-primary">Monitor Velocity Alerts:</strong> Rapid mention spikes can indicate breaking news or significant events.</p>
          </div>
          <div class="flex items-start">
            <span class="emoji mr-2">2️⃣</span>
            <p><strong class="text-primary">Watch Momentum Alerts:</strong> Early momentum signals can help you spot trends before they become mainstream.</p>
          </div>
          <div class="flex items-start">
            <span class="emoji mr-2">3️⃣</span>
            <p><strong class="text-primary">Check Sentiment Shifts:</strong> Sudden sentiment changes may indicate news or events affecting a stock.</p>
          </div>
          <div class="flex items-start">
            <span class="emoji mr-2">4️⃣</span>
            <p><strong class="text-primary">Review Due Diligence Posts:</strong> High-quality DD posts often precede significant price movements.</p>
          </div>
          <div class="flex items-start">
            <span class="emoji mr-2">5️⃣</span>
            <p><strong class="text-primary">Use Deep Dive:</strong> Always do a deep dive analysis before making any decisions. Look at the full picture, not just mentions.</p>
          </div>
          <div class="flex items-start">
            <span class="emoji mr-2">6️⃣</span>
            <p><strong class="text-primary">Cross-Reference with News:</strong> Check the News & Filings section to see if Reddit discussions align with official company announcements.</p>
          </div>
          <div class="flex items-start">
            <span class="emoji mr-2">7️⃣</span>
            <p><strong class="text-primary">Build a Watchlist:</strong> Add interesting tickers to your watchlist for easy access and monitoring.</p>
          </div>
          <div class="flex items-start">
            <span class="emoji mr-2">8️⃣</span>
            <p><strong class="text-primary">Remember:</strong> Reddit sentiment is just one data point. Always conduct your own research and never invest more than you can afford to lose.</p>
          </div>
        </div>
      </div>
      
      <!-- Contact Section -->
      <div class="card p-6 border-2 border-neon-cyan neon-glow-cyan">
        <h2 class="text-2xl font-bold mb-4 text-neon-cyan">
          <span class="emoji">📧</span> Need Help or Have Feedback?
        </h2>
        <div class="space-y-3 text-secondary">
          <p>
            Have a question, found a bug, or want to suggest a feature? We'd love to hear from you!
          </p>
          <p>
            Visit the <strong class="text-neon-cyan">Landing Page</strong> (click "Donate/Landing" in the header) 
            to find our contact section with options for bug reports, feature suggestions, and support requests.
          </p>
          <p class="text-sm">
            Or email us directly at: 
            <a 
              href="mailto:officialtkrscan@gmail.com" 
              class="text-neon-cyan hover:underline font-mono"
            >
              officialtkrscan@gmail.com
            </a>
          </p>
        </div>
      </div>
      
      <!-- Important Reminders Section -->
      <div class="card p-6 border-2 border-red-500 neon-glow-red">
        <h2 class="text-2xl font-bold mb-4 text-red-400">
          <span class="emoji">⚠️</span> Important Reminders
        </h2>
        <div class="space-y-3 text-secondary">
          <p>
            <strong class="text-red-400">This tool is for informational purposes only.</strong> It aggregates data from public sources 
            and should not be considered financial advice.
          </p>
          <p>
            <strong class="text-yellow-400">Data may be inaccurate or delayed.</strong> Always verify information independently before 
            making any investment decisions.
          </p>
          <p>
            <strong class="text-orange-400">Reddit sentiment does not predict stock prices.</strong> Social media discussions are 
            just one factor among many that can influence markets.
          </p>
          <p>
            <strong class="text-primary">Always do your own research</strong> and consult with qualified financial advisors before 
            making investment decisions.
          </p>
        </div>
      </div>
    </div>
  `;
}

